import { z } from 'zod';

/**
 * Result of the API's D1 connectivity probe.
 *
 * `ok: false` still returns 200 for the health endpoint itself; the caller is
 * expected to look at this field. Keeping it in the body (rather than mapping
 * it onto the status code) makes it possible to tell "the Worker is down" apart
 * from "the Worker is up but cannot reach its database".
 */
export const databaseProbeSchema = z.object({
  ok: z.boolean(),
  latencyMs: z.number().int().nonnegative(),
  /** Present only when `ok` is false. Never contains connection strings. */
  error: z.string().optional(),
});

export type DatabaseProbe = z.infer<typeof databaseProbeSchema>;

/**
 * Payload of `GET /api/v1/health`.
 *
 * This exists so that the four environments can be told apart from the outside:
 * if `environment` or `database.name` is not what the caller expects, the
 * deployment is pointing at the wrong resources.
 */
export const healthResponseSchema = z.object({
  /** `degraded` means the Worker is up but its D1 probe failed. */
  status: z.enum(['ok', 'degraded']),
  environment: z.enum(['dev', 'preview', 'staging', 'production']),
  /** Git SHA baked in at deploy time; `unknown` for local runs. */
  commitSha: z.string(),
  /** ISO 8601, server clock. */
  timestamp: z.string(),
  database: databaseProbeSchema,
  /**
   * Which place provider this deployment is configured with, and whether it
   * could serve a call right now — **dev and preview only**, absent elsewhere.
   *
   * Here rather than on the proxy because the proxy is behind sign-in since
   * P1, and `preview.yml` checks a labelled preview's key from outside with no
   * one signed in. Resolving the provider reads configuration only: nothing is
   * called upstream and nothing is counted.
   */
  places: z
    .object({
      /** `PLACES_PROVIDER` as configured: `google`, `mock`, `none`. */
      mode: z.string(),
      ready: z.boolean(),
      /** Why not, when `ready` is false: `missing_key`, `disabled`, … */
      reason: z.string().optional(),
    })
    .optional(),
  /**
   * The same for the link search behind 「候補を探す」 (2026-09-27, OQ-54):
   * `LINK_SEARCH_PROVIDER` and whether its key is there. Dev and preview only,
   * for `preview.yml` to check a `links-live` preview.
   */
  linkSearch: z
    .object({
      /** `tavily`, `mock`, `none`. */
      mode: z.string(),
      ready: z.boolean(),
      reason: z.string().optional(),
    })
    .optional(),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const environmentNameSchema = healthResponseSchema.shape.environment;
export type EnvironmentName = z.infer<typeof environmentNameSchema>;
